import { useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import { ORDER_TERMS } from "@/config/site";
import { MEDIA } from "@/data/media";
import { PAGE_TITLES } from "@/data/pages";
import { faqList } from "@/data/faqs";
import { Seo, JsonLd, serviceLd } from "@/components/site/Seo";
import { Breadcrumbs, Container, Eyebrow, PhotoFrame, Reveal, SectionHeading, WhatsAppButton } from "@/components/site/Primitives";
import { FaqSection, FinalCta, HowToOrder, RelatedLinks } from "@/components/site/Blocks";
import QuoteForm from "@/components/site/QuoteForm";

const AUDIENCES = [
  { t: "Businesses", d: "Staff uniforms, branded clothing and corporate gifts." },
  { t: "Schools & preschools", d: "Branded clothing for learners, staff and events." },
  { t: "Churches", d: "Matching clothing, candles and mugs for services and conferences." },
  { t: "Burial societies", d: "Matching T-shirts and golf shirts for members." },
  { t: "Families", d: "Reunion, memorial and celebration clothing and gifts." },
  { t: "NGOs & community groups", d: "Volunteer T-shirts and promotional items." },
  { t: "Event organisers", d: "Event clothing, commemorative mugs and gifts." },
];

export default function BulkOrders() {
  const [params] = useSearchParams();
  const path = "/bulk-orders";
  const description = "Bulk custom printing in Cape Town for businesses, schools, churches, burial societies, families and events. Group T-shirts, church clothing, uniforms and branded gifts. Request a bulk quote.";
  return (
    <>
      <Seo title="Bulk Printing Cape Town | Group T-Shirt & Church Clothing Printing | KhozaLand" description={description} path={path} image={MEDIA.mugsEvent.large} />
      <JsonLd data={serviceLd({ name: "Bulk custom printing and group orders", description, path })} />

      <section className="bg-white" data-testid="bulk-hero">
        <Container className="pt-6"><Breadcrumbs items={[{ name: "Bulk & Group Orders", path }]} /></Container>
        <Container className="grid gap-12 pb-16 pt-8 lg:grid-cols-12 lg:items-center lg:pb-24 lg:pt-12">
          <div className="lg:col-span-6">
            <Eyebrow>Bulk & group orders</Eyebrow>
            <h1 className="kl-h1 mt-5">Bulk Printing & Custom Group Orders</h1>
            <p className="mt-6 max-w-xl text-base leading-7 text-black/70 sm:text-lg sm:leading-8">
              Matching clothing and branded products for your whole group. KhozaLand accepts group and bulk enquiries from businesses, schools, churches, burial societies, families, NGOs and event organisers.
            </p>
            <p className="mt-5 inline-flex items-start gap-2 rounded-[12px] border border-black/10 bg-[#F7F7F7] px-4 py-3 text-sm leading-6" data-testid="bulk-discount-note"><Check className="mt-1 h-4 w-4 shrink-0" aria-hidden="true" /> {ORDER_TERMS.bulkDiscount.text}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href="#quote-form" className="kl-btn kl-btn-dark h-12 px-6" data-testid="bulk-hero-quote-button">Request a Bulk Quote</a>
              <WhatsAppButton messageKey="bulk" className="h-12" testId="bulk-hero-whatsapp-button" />
            </div>
          </div>
          <div className="grid grid-cols-2 items-start gap-4 lg:col-span-6">
            <PhotoFrame media={MEDIA.mugsEvent} ratio="4/5" eager sizes="(min-width:1024px) 24vw, 46vw" />
            <PhotoFrame media={MEDIA.candlesChurch} ratio="4/5" className="mt-10" sizes="(min-width:1024px) 24vw, 46vw" />
          </div>
        </Container>
      </section>

      <section className="bg-[#F7F7F7] py-16 lg:py-24" aria-labelledby="who-title">
        <Container>
          <SectionHeading eyebrow="Who we serve" title="Group orders for every kind of organisation" id="who-title" />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.t} delay={(i % 4) * 60} className="kl-card p-6">
                <h3 className="text-lg font-semibold">{a.t}</h3>
                <p className="mt-2 text-sm leading-6 text-black/65">{a.d}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <HowToOrder />

      <section id="quote-form" className="scroll-mt-24 bg-[#F7F7F7] py-16 lg:py-24" aria-labelledby="bulk-form-title">
        <Container className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="Bulk quotation" title="Request a Bulk Quote" lead="Tell us about your organisation, product, quantity and date. Plan ahead — urgent orders are not accepted." id="bulk-form-title" />
            <ul className="mt-6 space-y-2 text-sm text-black/70">
              {["Your organisation name", "Product type & quantity", "Sizes & colours", "Logo or design", "Required date & delivery location"].map((x) => (<li key={x} className="flex gap-2"><Check className="mt-0.5 h-4 w-4" aria-hidden="true" /> {x}</li>))}
            </ul>
          </div>
          <div className="lg:col-span-8">
            <QuoteForm variant="bulk" defaultCategory={params.get("category") || "bulk-group"} defaultProduct={params.get("product") || ""} sourcePage={path} />
          </div>
        </Container>
      </section>

      <FaqSection faqs={faqList(["bulk", "churchSociety", "minimum", "production", "urgent", "deposit", "delivery"])} />
      <RelatedLinks links={["/custom-tshirt-printing", "/uniforms-embroidery", "/corporate-gifts"]} titles={PAGE_TITLES} />
      <FinalCta category="bulk-group" messageKey="bulk" primaryLabel="Request a Bulk Quote" />
    </>
  );
}
